/**
 * `.pxm` — the authored pixel mask, and the one file format in this project.
 *
 * ==== WHY A FILE AND NOT A FUNCTION =====================================
 *
 * `rigidShapes.ts` bakes a `ShapeSpec` into 32 pre-rotations with a boundary
 * normal per edge pixel. That is the asset issue #115 calls "the one hard
 * forcing function", and the reason is arithmetic rather than taste: the crate
 * and the hatch were written as `for` loops — `edge || y === 6` — because a
 * crate and a plate are the two shapes a `for` loop can express. A minecart, a
 * barrel, a ladder segment, a corpse-cart wheel: none of them are a predicate
 * over `(x, y)`, and each one written as source is a hundred lines of nested
 * conditionals nobody can read back as a picture. Past the first crate, the
 * shape has to be *drawn*.
 *
 * So a shape is a grid of characters in a text file, and the file is the
 * authored artefact. `crate.pxm` reads as a crate when you open it, which is
 * the entire argument.
 *
 * ==== THE ROUND-TRIP CONTRACT ===========================================
 *
 * **`serialize(parse(t)) === t`, byte for byte, and it is a test rather than a
 * hope.** A tool that rewrites a file it did not fully understand silently
 * destroys work — a dropped comment, a reordered legend, a lost trailing row —
 * and the loss is invisible until somebody diffs a file they did not expect to
 * have changed. So the format has **exactly one spelling of any mask**: the
 * parser rejects the near-misses instead of quietly normalising them, which
 * makes the round trip a property of the format rather than a property of the
 * serialiser being careful. `tests/shapeMask.test.ts` holds it in both
 * directions, on the shipped files and on generated ones.
 *
 * Comment lines are part of that contract, not decoration stripped on load. The
 * argument for 13×13 and for a hollow wall lives in `crate.pxm`'s header, where
 * the next person to edit the crate will actually see it.
 *
 * ==== WHY THE SERIALISER LIVES HERE, WITH NO RUNTIME CALLER =============
 *
 * `parse` is called by `rigidShapes.ts`; `serialize` is called only by
 * `tools/pixel.mjs` and by the tests. It stays in this module anyway, because
 * the alternative is a second implementation of the format living in the tool,
 * and then the round-trip test proves that two programs agree rather than that
 * the format is lossless. One writer, one reader, one file.
 *
 * ==== WHAT SHIPS ========================================================
 *
 * The `.pxm` files are pulled in with Vite's `?raw`, so they are inlined into
 * the bundle as string literals at build time. No fetch, no asset directory at
 * runtime, no new dependency, and no ordering hazard against
 * `registerMaterials()` — a document carries material *names*, and the ids are
 * resolved by `specFor` at bake time, which is already lazy for exactly this
 * reason.
 *
 * ==== DEFERRED CLASSES, AND WHY THEY LIVE IN THIS FORMAT ================
 *
 * A legend entry may name a **deferred class** — `@host`, `@shell`, `@platform`,
 * `@gate`, `@vein`, `@glow`, `@fill` — instead of a material. `@host` is the
 * important one and it is Noita's `#ffffff`, verbatim from their wiki: *"The
 * color FFFFFF uses materials from the biome it loads in, so you dont have to
 * draw it yourself."* `docs/19` §3.2 measures how much of an authored Noita cave
 * room is drawn that way — **21-34 % of the body and 36-82 % of the outermost
 * one-pixel ring** — and §8.3 calls it *"the single cheapest item here and we do
 * not have it"*.
 *
 * It is an extension of *this* format rather than a second format, and the
 * reason is #136: a fortnight ago a second spelling of an existing thing cost a
 * day, and `docs/15` §3.2 rules on it. The lexing is what keeps the extension
 * free — a material name is `[A-Z][A-Z0-9_]*` and a class is `@[a-z][a-z0-9]*`,
 * so the two vocabularies cannot collide, a file says which one it means in its
 * first character, and `serialize(parse(t)) === t` is unaffected because the
 * token is echoed back exactly as written.
 *
 * **The class vocabulary is closed and it is validated at parse time**, which
 * is the one place this differs from materials, deliberately. A material name is
 * *not* checked here — a document carries names and the build resolves them, so
 * `specFor` is where `UNOBTANIUM` fails. A class is not a name from the build;
 * it is a word in the format, and `DEFERRED_CLASSES` below is the whole list. A
 * typo in it is therefore a syntax error rather than a deferred one, which is
 * what stops `pixel tile new --fill @hsot` writing a file at all.
 *
 * Neither consumer takes both vocabularies, and each says so: `specFor` refuses
 * a class (a rigid crate has no host biome to defer to), and the tile side —
 * `src/world/tiles/tileMask.ts` — refuses a material (a `Kind` is not a
 * material, and a tile that named one would be asking the room lattice to write
 * a colour).
 *
 * ==== VERSIONS, AND WHY THE NUMBER IS NOT THE WRITER'S TO CHOOSE =========
 *
 * `docs/29-pxm-format.md` is the written specification and §3 is this. The short
 * of it: a file **may** open with `pxm <n>`, absence means version 1, and the
 * number is **derived from the document rather than stored on it** — a file
 * declares the lowest version whose grammar can read it and no higher.
 *
 * That one rule is what lets a version field exist inside a format whose whole
 * value is that every document has exactly one spelling. Version 1 is spelled by
 * *absence*, so the four shipped files and every `.pxm` written before versioning
 * existed stay valid unchanged; and a document using nothing newer than version 1
 * cannot be written two ways, because declaring the line would be declaring a
 * version the content does not need.
 *
 * `pxmVersion` is that derivation and `serializeShapeMask` calls it rather than
 * reading a field, so a declared version and the content it describes cannot
 * drift. Version 2 is anchors, and a document with no anchors is version 1
 * whatever else is in it. Version 4 is a legend pool (issue #183, below) — a
 * document with no pool declaration is nothing newer than version 3 whatever
 * else is in it.
 *
 * **A version above this reader's is refused, not skipped**, and the refusal is
 * the specification rather than a shortcoming of it: bytes a reader skipped are
 * bytes it cannot re-emit, so a tolerant reader and `serialize(parse(t)) === t`
 * cannot both exist. A reader that quietly dropped a later version's declarations
 * would hand back a *downgraded* file, and the loss would be invisible until
 * somebody diffed a file they only meant to open.
 *
 * `.plan` shares this discipline and does not implement the production — see §3.1
 * of the spec. Because version 1 is absence, a format may adopt the rule at the
 * moment it is first extended and every file written before that moment is
 * already correct, so implementing an unreachable grammar now would only be the
 * untested spelling `planText.ts`'s own `ANGLE_DIRS` docblock declines to ship.
 *
 * ==== ANCHORS, AND WHY THEY ARE NOT GRID DATA ===========================
 *
 * **A grid cannot say "the door goes here."** `src/world/greatTree.ts` declares
 * `TREE_HOLLOW_SITE` — a coordinate in TypeScript describing a place in a
 * drawing — and that is backwards, and it gets worse with every authored room.
 * Version 2 is one production: a named coordinate with a role and a set of
 * claims, declared beside the legend.
 *
 * Beside the legend rather than inside the grid, and the reason is arithmetic
 * again rather than taste: a room has three or four named places and `w * h`
 * cells, so a second grid would be 8 192 characters carrying four facts, and it
 * would end the property the format exists for — that the file reads as a
 * picture — by stacking two pictures nobody can read against each other.
 * `docs/29-pxm-format.md` §8 keeps that argument and the two others that were
 * weighed with it.
 *
 * **A claim is verified by the parser**, which is the whole difference between
 * an anchor and a comment: a `.pxm` that carries a claim cannot be wrong about
 * it. `tests/greatTree.test.ts` asserts four things about `TREE_HOLLOW_SITE` —
 * standable, clear to the body's full height, dry, and walkable to from spawn.
 * The first two are `floor` and `headroom=22` and this format guarantees them at
 * load. The last two are properties of a *placement*: water arrives after the
 * drawing, from the host's pools, into air the drawing left, and a route from
 * spawn is a fact about a world the drawing has not been put into yet. An anchor
 * makes those checkable by naming the point, which is the most a drawing can
 * honestly promise about a world it has not met.
 */

import { VOID, type MatId } from './materials';
import type { ShapeSpec } from './rigidShapes';

/** The character that always means "no cell here". Never appears in a legend line. */
export const VOID_CHAR = '.';

/**
 * The highest version this reader implements, and the whole of what a `pxm`
 * line may say.
 *
 * A file declaring more than this is refused rather than partially read — see
 * the header, and `docs/29-pxm-format.md` §3 rule 4.
 */
export const PXM_READER_VERSION = 4;

/**
 * What kind of thing belongs at an anchor. Closed, and validated at parse time.
 *
 * ==== THIS LIST IS THE HINTS LAYER ======================================
 *
 * Where enemies spawn, where loot goes, what is decorative: none of it was
 * expressible, so all of it lived in the generator, which means **an authored
 * room could not say anything about what should be in it**. It is a role on an
 * anchor, and three designs were weighed against that (`docs/29-pxm-format.md`
 * §8 keeps all three):
 *
 *  - **Not a second grid.** A hint payload is sparse and *pointlike* — a room
 *    has four named places and 8 192 cells — so a `hints` block would be 8 192
 *    characters carrying four facts, 99 % of them `.`. It would also end the
 *    property the format exists for, that the file reads as a picture, by
 *    stacking two pictures nobody can read against each other; and it doubles
 *    the round trip's surface, since two grids must stay in lockstep with one
 *    `w`/`h` and every way they can disagree is a new malformed document.
 *  - **Not a second class per cell.** Making a cell mean `@shell` *and*
 *    `spawn` needs either two characters per cell — which ends the
 *    one-cell-one-column alignment that makes a grid legible — or a second
 *    legend axis, which is the second grid wearing a hat.
 *  - **Not a `.hints` file beside the `.pxm`.** That is #136 exactly: a second
 *    spelling of an existing thing, two files to keep in step, and nothing
 *    structural stopping them disagreeing about `w` and `h`.
 *
 * What settles it is that **an anchor is already a named point in the drawing**,
 * so a hints layer built as a list of named points would have been a second
 * spelling of one. And the case for a point rather than a field is not ours to
 * make: LDtk's entity layers and Tiled's object layers are lists of named points
 * with fields rather than additional tile grids, designed independently, solving
 * exactly this.
 *
 * **A role names a category and never a particular thing.** Which creature,
 * which container, which prop is a name from the *build* — the same kind of word
 * as a material name, which this format also declines to check — and a later
 * version may add a tag production to carry one. The version discipline is what
 * makes that cheap to do later rather than expensive to get wrong now.
 *
 * A linear scan rather than a lookup table, for the reason `deferredClassOf`
 * gives: there is no key to index, so `anchorRoleOf('toString')` cannot answer
 * `Function.prototype.toString`. `docs/25` §2.1 found that live four times.
 */
export const ANCHOR_ROLES = ['site', 'spawn', 'loot', 'decor'] as const;
export type AnchorRole = (typeof ANCHOR_ROLES)[number];

/**
 * Every claim an anchor may make, **in the order a file must write them**.
 *
 * Alphabetical, which is a rule an author can apply without consulting
 * anything. A set written as a sequence has one canonical order or it has
 * several spellings, and §2 of the spec does not allow several.
 *
 * `headroom` is the one that takes a value; the table below is the whole of
 * that distinction.
 */
export const ANCHOR_CLAIMS = ['floor', 'headroom', 'solid'] as const;
export type AnchorClaimName = (typeof ANCHOR_CLAIMS)[number];

/** The claims that take `=n`, spelled once. */
const VALUED_CLAIMS: readonly string[] = ['headroom'];

/**
 * The seven combo keys a `.weapon`-kind `.pxm` (version 3, issue #168) may
 * name in a `node` line — the same seven `buildCombo`'s own `SKELETON` table
 * builds, in `weaponData.ts`. Spelled here rather than imported: that file is
 * `src/combat/`'s and this one is `src/sim/`'s, and the dependency runs the
 * other way (combat resolves `.pxm` documents, not the reverse) — the same
 * reason `deferredClassOf`'s vocabulary lives beside its own parser rather
 * than pulled from `rooms.ts`, which is the file that actually interprets it.
 * `tests/weaponMask.test.ts` pins this list against `weaponData.ts`'s own
 * skeleton order.
 */
export const WEAPON_NODE_KEYS = ['light1', 'light2', 'light3', 'heavy', 'dash', 'air', 'dair'] as const;
export type WeaponNodeKey = (typeof WEAPON_NODE_KEYS)[number];

/** The position of a node key in skeleton order, or `-1`. Linear scan; see `WEAPON_NODE_KEYS`. */
function nodeKeyOrder(value: string): number {
  for (let k = 0; k < WEAPON_NODE_KEYS.length; k++) if (WEAPON_NODE_KEYS[k] === value) return k;
  return -1;
}

/**
 * The four swing curves a `node` line's `ease` field may name. Spelled here
 * for the same reason `WEAPON_NODE_KEYS` is — `src/combat/types.ts`'s
 * `EaseKind` restates this list independently rather than importing it,
 * matching every other number/vocabulary rule this format states more than
 * once on purpose (see the header on why `intToken` is not imported either).
 */
export const WEAPON_EASE_KINDS = ['linear', 'easeIn', 'easeOut', 'easeInOut'] as const;
export type WeaponEaseKind = (typeof WEAPON_EASE_KINDS)[number];

/** Every field a `node` line's numeric part may set, **in the order a file must write them**. */
const NODE_NUMERIC_FIELDS = ['w', 'a', 'r', 'wm', 'am', 'rm', 'arcStart', 'arcEnd'] as const;
type NodeNumericField = (typeof NODE_NUMERIC_FIELDS)[number];

/**
 * A weapon's one authored feel value (issue #168 phase 1's "feel" group).
 *
 * A single line rather than a repeatable declaration, because there is
 * exactly one of it per weapon — `weight` is docs/04's "the only handling
 * knob authored per weapon" — where `anchor`/`node` are lists because a
 * drawing has several places and several combo nodes.
 */
export interface WeaponFeel {
  readonly weight: number;
}

/**
 * One `node` line — a combo key plus whichever of the eight fields (see
 * `NODE_NUMERIC_FIELDS`) and `ease` the author set. Every field is optional;
 * at least one is required, checked at parse time the same way an anchor's
 * claim list is.
 */
export interface WeaponNodeDecl {
  readonly key: WeaponNodeKey;
  readonly w?: number;
  readonly a?: number;
  readonly r?: number;
  readonly wm?: number;
  readonly am?: number;
  readonly rm?: number;
  readonly arcStart?: number;
  readonly arcEnd?: number;
  readonly ease?: WeaponEaseKind;
}

/**
 * One claim, as the file spells it.
 *
 * `n` is `null` rather than absent for a valueless claim, so the two shapes are
 * one shape: a `{ name }` and a `{ name, n: undefined }` compare equal under
 * some matchers and not others, and a round-trip test that cannot tell them
 * apart is a round-trip test with a hole in it.
 */
export interface AnchorClaim {
  readonly name: AnchorClaimName;
  readonly n: number | null;
}

/**
 * A named coordinate in a drawing, with a role and a set of checked claims.
 *
 * **The thing a grid could not say.** `TREE_HOLLOW_SITE` is a coordinate in
 * TypeScript describing a place in a drawing, which is backwards, and it gets
 * worse per authored room. An anchor is the drawing saying it itself.
 *
 * Coordinates are grid-local — `0 ≤ x < w`, `0 ≤ y < h`, origin top-left, `+y`
 * down — so an anchor is read off the file by counting.
 */
export interface ShapeAnchor {
  readonly name: string;
  readonly x: number;
  readonly y: number;
  readonly role: AnchorRole;
  readonly claims: readonly AnchorClaim[];
}

/** The role a token names, or `undefined`. Linear scan; see `ANCHOR_ROLES`. */
export function anchorRoleOf(value: string): AnchorRole | undefined {
  for (const r of ANCHOR_ROLES) if (r === value) return r;
  return undefined;
}

/** The position of a claim in canonical order, or `-1`. Linear scan, same reason. */
function claimOrder(value: string): number {
  for (let k = 0; k < ANCHOR_CLAIMS.length; k++) if (ANCHOR_CLAIMS[k] === value) return k;
  return -1;
}

/**
 * The lowest `.pxm` version whose grammar can read this document.
 *
 * **Derived, never stored.** A `ShapeMaskDoc` has no version field, so a
 * declared version and the content it describes cannot disagree; the serialiser
 * calls this instead of reading a field, and the parser checks a declared
 * version against it. One function answers both halves, which is why the round
 * trip survives a version line at all — see the header and spec §3 rule 3.
 */
export function pxmVersion(doc: ShapeMaskDoc): number {
  if (doc.pools.length > 0) return 4;
  if (doc.feel !== undefined || doc.nodes.length > 0) return 3;
  return doc.anchors.length > 0 ? 2 : 1;
}

/**
 * Everything about an anchor set that is true or false of a *document* rather
 * than of a line of text — including whether its claims are true of the grid.
 *
 * Returns the first problem as a sentence, or `null`. Shared by the parser and
 * by `docFromGrid` so that a document built in memory and a document read off
 * disk are held to one rule; a second copy of "what a claim means" is how a
 * format grows a second answer to it.
 *
 * **Claims are stated in void and not-void, and that is the format's boundary
 * rather than a shortage of imagination.** Occupancy is the only fact this
 * format owns: whether a cell is *matter* is a question about a material name
 * or a class, and both of those are resolved by the host, so `floor` can say
 * there is something underfoot and cannot say it is dry.
 */
function anchorProblem(
  w: number,
  h: number,
  rows: readonly string[],
  a: ShapeAnchor,
): string | null {
  if (!/^[a-z][a-z0-9_]*$/.test(a.name)) return `\`${a.name}\` is not an anchor name`;
  if (anchorRoleOf(a.role) === undefined) {
    return `\`${a.role}\` is not a role — the format has ${ANCHOR_ROLES.join(', ')}`;
  }
  if (!Number.isInteger(a.x) || !Number.isInteger(a.y) || a.x < 0 || a.y < 0 || a.x >= w || a.y >= h) {
    return `anchor \`${a.name}\` is at ${a.x},${a.y}, which is outside a ${w}x${h} grid`;
  }
  let last = -1;
  for (const c of a.claims) {
    const k = claimOrder(c.name);
    if (k < 0) return `\`${c.name}\` is not a claim — the format has ${ANCHOR_CLAIMS.join(', ')}`;
    if (k === last) return `anchor \`${a.name}\` claims \`${c.name}\` twice`;
    if (k < last) {
      return `anchor \`${a.name}\`: claims are written in alphabetical order, so \`${c.name}\` comes earlier`;
    }
    last = k;
    const valued = VALUED_CLAIMS.includes(c.name);
    if (valued && (c.n === null || !Number.isInteger(c.n) || c.n < 1)) {
      return `anchor \`${a.name}\`: \`${c.name}\` takes a whole number of cells, one or more`;
    }
    if (!valued && c.n !== null) return `anchor \`${a.name}\`: \`${c.name}\` takes no value`;
    // …and now against the drawing itself. A `.pxm` that carries a claim
    // cannot be wrong about it: this is the assertion that makes an anchor
    // worth more than a comment.
    if (c.name === 'solid' && rows[a.y][a.x] === VOID_CHAR) {
      return `anchor \`${a.name}\` claims \`solid\` and its own cell is empty`;
    }
    if (c.name === 'floor') {
      if (a.y + 1 >= h) return `anchor \`${a.name}\` claims \`floor\` and sits on the bottom row`;
      if (rows[a.y + 1][a.x] === VOID_CHAR) {
        return `anchor \`${a.name}\` claims \`floor\` and the cell under it is empty`;
      }
    }
    if (c.name === 'headroom') {
      const n = c.n as number;
      if (a.y - n + 1 < 0) {
        return `anchor \`${a.name}\` claims \`headroom=${n}\` and there are only ${a.y + 1} cells above it`;
      }
      for (let d = 0; d < n; d++) {
        if (rows[a.y - d][a.x] !== VOID_CHAR) {
          return `anchor \`${a.name}\` claims \`headroom=${n}\` and cell ${a.x},${a.y - d} is not empty`;
        }
      }
    }
  }
  return null;
}

/**
 * Every anchor problem in a document, as sentences. Empty when there are none.
 *
 * Exported for `pixel mask check` and `pixel tile check`, which report rather
 * than throw, and for tests. The parser refuses on the first one instead: a
 * document whose claims are false is not a document this format accepts.
 */
export function anchorProblems(doc: ShapeMaskDoc): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const a of doc.anchors) {
    if (seen.has(a.name)) out.push(`anchor \`${a.name}\` declared twice — a name addresses one place`);
    seen.add(a.name);
    const p = anchorProblem(doc.w, doc.h, doc.rows, a);
    if (p !== null) out.push(p);
  }
  return out;
}

/** Write one anchor back out, in the one spelling. */
function anchorText(a: ShapeAnchor): string {
  const claims = a.claims.map((c) => (c.n === null ? c.name : `${c.name}=${String(c.n)}`));
  return ['anchor', a.name, String(a.x), String(a.y), a.role, ...claims].join(' ');
}

/** The sigil that makes a legend value a class rather than a material name. */
export const DEFERRED_PREFIX = '@';

/**
 * Every deferred class the format has, and the whole of the vocabulary.
 *
 * Spelled without the sigil; `@` + one of these is what a file writes. The list
 * is closed on purpose — see the header. `host` is the deferral Noita and
 * Starbound both arrived at independently (`docs/17` §2.5 item 2); the rest are
 * the same deferral applied to one `BiomePalette` slot each, so that a drawing
 * carrying none of the twelve rosters' materials still reads as native in all
 * twelve. `src/world/rooms.ts` owns the class → `Kind` table and
 * `src/world/worldgen.ts` `materialise` owns the `Kind` → material one.
 *
 * ==== WHY SEVEN, AND WHY THE COUNT IS NOT A CHOICE ======================
 *
 * `docs/19` §8.3a recorded the ceiling this list was written to lift: *"an
 * authored solid has exactly one class — `Kind.SHELL` — and it resolves to
 * `palette.shell`, which `tests/paletteStructure.test.ts` records at a 5 %
 * ceiling with `chlorine_fen` already at 4.951 %"*. One slot, and full. So the
 * question is how many more, and the honest answer is that the palette decides
 * it rather than taste:
 *
 *  - **`BiomePalette` has ten slots.** `primary` and `secondary` are one class
 *    between them and not two — #143 made *which* of the pair a place is made of
 *    a property of a `MATERIAL_SPAN` region, and a drawing allowed to override
 *    that per cell would be re-drawing the quilt by hand. That is `@host`.
 *  - **`liquid`, `liquid2` and `hazard` are excluded**, and by a rule rather
 *    than by omission: a `kind[]` cell is *static matter* — `materialise` writes
 *    `Flag.STATIC` on everything that is not `Kind.OPEN` — and a pool that does
 *    not flow is not a pool. Liquids are `pools`' and `hazardPockets`' to place,
 *    after the mask, into the air the drawing left them.
 *  - That leaves **`shell`, `platform`, `fill`, `accents`, `glow`** — five, and
 *    every one of them is a material worldgen is *already* scattering through
 *    the same window (`edgeWalls`, `ledges`, `deposits`, the ore pass in
 *    `materialise`, `glowVeins`). This is the whole affordability argument and
 *    §12.6 of `docs/24` measures it: **a drawn room adds no material to a window
 *    that was not in it already.** It moves the biome's own budget from scatter
 *    into mass, which is what `docs/19` §3.2 measures Noita doing.
 *  - Plus **`@gate`**, the one class that is not a palette slot at all:
 *    ADAMANT_SEAL, identical in twelve biomes on purpose, because a lock that
 *    read as a different thing in each place would not be a lock.
 *
 * 1 + 5 + 1 = **7**, and there is no eighth to add without either authoring a
 * new palette slot in twelve biomes or letting a drawing place matter that
 * flows.
 *
 * **§3.2's "median 4-8 distinct colours" is not this number** and must not be
 * read as it: it is a measurement of one *scene*, and it belongs on the drawing.
 * `tests/tileMask.test.ts` asserts it there, per shipped tile, where it can
 * actually fail.
 */
export const DEFERRED_CLASSES = [
  'host', 'shell', 'platform', 'gate', 'vein', 'glow', 'fill',
] as const;
export type DeferredClass = (typeof DEFERRED_CLASSES)[number];

/**
 * The class a legend value names, or `undefined` if it names a material.
 *
 * Exported because both resolvers have to ask, and a second copy of the sigil
 * rule is how a format grows a second spelling. A linear scan of four strings
 * rather than a lookup table, which is the `own()` rule in `planText.ts` reached
 * by construction instead of by remembering: there is no key to index, so
 * `deferredClassOf('@toString')` cannot answer `Function.prototype.toString`.
 */
export function deferredClassOf(value: string): DeferredClass | undefined {
  if (!value.startsWith(DEFERRED_PREFIX)) return undefined;
  const bare = value.slice(DEFERRED_PREFIX.length);
  for (const c of DEFERRED_CLASSES) if (c === bare) return c;
  return undefined;
}

/**
 * **Legend pools (version 4, issue #183): per-placement material variation,
 * geometry untouched.**
 *
 * ==== WHAT NOITA DOES THAT THIS FORMAT DID NOT =========================
 *
 * `docs/19` §3.4 and `authoredTileAt`'s own docblock in `src/world/rooms.ts`
 * record a refusal: an authored tile is a constant, the same bytes at every
 * placement, on purpose — mirroring, rotating or re-rolling the *grid* is a
 * second description of one geometry and this project declined that. But the
 * refusal was about geometry. Noita's own per-placement variation was never a
 * geometric one either — it is a **material** re-roll, and that half was
 * simply never built. A pool is that half, and only that half: the grid stays
 * byte-identical at every placement; what varies is which class a pool
 * character resolves to.
 *
 * ==== WHY A POOL ENTRY IS A DEFERRED CLASS, NOT A MATERIAL NAME =========
 *
 * The issue that proposed this spells an illustrative pool as
 * `@pool RUSTED_IRON:3 IRON:1` — materials. That is illustrative of *intent*,
 * not of the grammar this format can carry: a tile's legend never names a
 * material at all, by design (`kindGrid`'s header) — a `Kind` is not a
 * material, and letting a tile name one would be asking the room lattice to
 * write a colour. So a pool entry is one of `DEFERRED_CLASSES` — `@fill:3
 * @vein:1` rather than a material pair — and because each class resolves to a
 * *different* `Kind`, which `materialise` later paints as a genuinely
 * different biome material, rolling between classes is already a real,
 * visible re-roll. It reuses the entire existing class → `Kind` →
 * biome-material pipeline (`kindForClass`, `materialise`) with no new
 * resolution logic, and it composes with `@host`'s own per-biome variation
 * rather than replacing it: a pool entry may itself be `@host`.
 *
 * ==== WHY POOLS ARE A SEPARATE FIELD, NOT A THIRD LEGEND SHAPE ==========
 *
 * `ShapeMaskDoc.legend` keeps meaning exactly what it has always meant — a
 * `[char, value]` pair naming one material or one class, resolved once, the
 * same for every placement. A pool character resolves to *different* things
 * at different placements, which is a different kind of fact and not a third
 * spelling of the same field; every existing reader of `legend` stays
 * correct without knowing pools exist.
 *
 * ==== WHY A POOL LINE COMES AFTER EVERY PLAIN LEGEND LINE ================
 *
 * A pool character is declared with the same `legend <char> …` prefix a
 * material or class is, and the parser accepts either production in the same
 * position — but a plain legend line **MUST NOT** follow a pool line (R56).
 * That is the round-trip contract's own requirement rather than a taste
 * call: `legend` and `pools` are two arrays, so serialising them back as
 * "every legend entry, then every pool entry" only reproduces the original
 * file if the file was written that way — the same reasoning that already
 * puts every `anchor` line after every `legend` line and every `node` line
 * after every `feel` line. `reliquary.pxm`'s `@fill` was already the last
 * legend line before it became a pool, so the shipped file needed no
 * reordering.
 *
 * ==== SEED, AND WHY IT NEVER REACHES THIS FILE ===========================
 *
 * A pool is a *set of choices*, not a choice. Resolving it into one class
 * needs a seed and a placement, and neither exists yet at parse time — a
 * `ShapeMaskDoc` is one file, read once, shared by every world at every seed.
 * The roll happens once per pool per placement in `stampAuthored`
 * (`src/world/rooms.ts`), seeded off the tile's own placement seed exactly
 * the way `authoredTileAt`'s own room choice already is. The tile stays
 * seed-free; the placement owns the roll.
 */
export interface LegendPoolEntry {
  readonly cls: DeferredClass;
  readonly weight: number;
}

/**
 * A legend character that resolves to one of several classes, chosen per
 * placement. `entries` is at least two — see the header above — in file
 * order, which is also serialisation order.
 */
export interface LegendPool {
  readonly ch: string;
  readonly entries: readonly LegendPoolEntry[];
}

/** A parsed `.pxm`, in the exact structure the file spells. */
export interface ShapeMaskDoc {
  /**
   * Leading `#` comment lines, verbatim and including the `#`.
   *
   * Kept because losing them is the failure this format is most likely to
   * have: the header is the only place a shape's *reasons* can live.
   */
  readonly header: readonly string[];
  readonly name: string;
  readonly w: number;
  readonly h: number;
  /**
   * Legend entries in file order, `[char, value]`. Excludes `VOID_CHAR`.
   *
   * A value is either a material name (`WOOD`) or a deferred class (`@host`).
   * `deferredClassOf` is the only reader of that distinction.
   */
  readonly legend: readonly (readonly [string, string])[];
  /**
   * Legend characters that resolve to a class chosen per placement, in file
   * order. Empty for every document version 1-3 ever written or shipped —
   * see the docblock over `LegendPool` for why this is a field of its own
   * rather than a third shape `legend`'s values can take.
   */
  readonly pools: readonly LegendPool[];
  /**
   * Named coordinates, in file order. Empty for a version-1 document.
   *
   * Not grid data, which is why they are a list beside the grid rather than a
   * second grid over it: a room has three or four of them, not `w * h`. The
   * argument in full is `docs/29-pxm-format.md` §8.
   */
  readonly anchors: readonly ShapeAnchor[];
  /**
   * The weapon's one authored feel value, or `undefined` for a version-1/2
   * document — every mask and tile ever shipped. Issue #168 phase 1.
   */
  readonly feel?: WeaponFeel;
  /**
   * Per-node timing/swing overrides, in file order. Empty for a document
   * with no `node` lines — every mask and tile, and a weapon that authors
   * `feel` alone.
   */
  readonly nodes: readonly WeaponNodeDecl[];
  /** `h` rows of exactly `w` characters. */
  readonly rows: readonly string[];
}

class MaskError extends Error {}

function fail(where: string, line: number, msg: string): never {
  throw new MaskError(`${where}:${line}: ${msg}`);
}

/**
 * An integer in the one spelling the format allows.
 *
 * `String(Number(tok)) === tok` is the whole rule and it is the language's own
 * shortest round-tripping form, so `007`, `+7`, `7.0` and `1e1` are rejected at
 * no cost in precision. Written out here rather than imported from
 * `planText.ts`: the rule is the same on purpose and both files say so, but a
 * `.pxm` parser that imported a `.plan` parser to read a number would be
 * coupling two formats that share a discipline and nothing else.
 */
function intToken(where: string, line: number, field: string, tok: string): number {
  const n = Number(tok);
  if (!Number.isInteger(n) || String(n) !== tok) {
    fail(where, line, `${field}: ${JSON.stringify(tok)} is not a whole number in canonical form`);
  }
  return n;
}

/**
 * A (possibly fractional, possibly negative) number in the one spelling the
 * format allows — `weight` and a swing arc's angles, version 3's own
 * production, are the first values in this format that are not grid-relative
 * integers.
 *
 * `String(Number(tok)) === tok` is the same rule `intToken` states, widened
 * to admit a sign and a fraction; it still rejects `7.0`, `+7`, `.5`, `07`
 * and `1e1` at no cost in precision. Independently restated for `intToken`'s
 * own reason — see its docblock — rather than imported from `planText.ts`
 * or `motionText.ts`, which state the identical rule a third and fourth time
 * for the same reason.
 */
function decimalToken(where: string, line: number, field: string, tok: string): number {
  const n = Number(tok);
  if (!Number.isFinite(n) || String(n) !== tok) {
    fail(where, line, `${field}: ${JSON.stringify(tok)} is not a number in canonical form`);
  }
  return n;
}

/**
 * Parse a `.pxm` document.
 *
 * Strict on structure and deliberately unhelpful about near-misses: a parser
 * that accepts two spellings of the same mask cannot promise a round trip. The
 * only tolerances are the ones a text editor imposes without asking — CRLF line
 * endings, and a missing final newline.
 *
 * `where` names the file in error messages. A stack trace pointing at this
 * function tells you nothing about which of eleven shapes is malformed.
 */
export function parseShapeMask(text: string, where = '<pxm>'): ShapeMaskDoc {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  if (lines.length > 0 && lines[lines.length - 1] === '') lines.pop();

  let i = 0;

  // The version line, if there is one, is the **first** line of the file —
  // ahead of the header comments, because a reader has to know which grammar it
  // is reading before it reads anything, and this is the only position where
  // that is true without promising that some other production never changes
  // meaning. `docs/29-pxm-format.md` §3 rule 1.
  let declaredVersion = 1;
  if (lines.length > 0 && lines[0].startsWith('pxm')) {
    const vm = /^pxm (\S+)$/.exec(lines[0]);
    if (vm === null) fail(where, 1, `expected \`pxm <version>\`, got ${JSON.stringify(lines[0])}`);
    // Canonical form, the same rule `planText.ts` states for every number in a
    // `.plan`: `pxm 02` and `pxm 2.0` are second spellings of one version.
    if (!/^[1-9][0-9]*$/.test(vm[1]) || String(Number(vm[1])) !== vm[1]) {
      fail(where, 1, `version: ${JSON.stringify(vm[1])} is not a version number in canonical form`);
    }
    declaredVersion = Number(vm[1]);
    if (declaredVersion === 1) {
      fail(where, 1, 'version 1 is spelled by leaving the `pxm` line out, so `pxm 1` is a second spelling of it');
    }
    if (declaredVersion > PXM_READER_VERSION) {
      // Refused rather than partially read. See the header: a reader that
      // skipped what it did not understand could not write the file back.
      fail(
        where, 1,
        `this file declares .pxm version ${declaredVersion} and this reader implements ${PXM_READER_VERSION}`,
      );
    }
    i = 1;
  }

  const header: string[] = [];
  while (i < lines.length && lines[i].startsWith('#')) header.push(lines[i++]);

  if (i >= lines.length) fail(where, i + 1, 'no `shape` line');
  // A version line down here is the one near-miss worth naming rather than
  // reporting as a malformed `shape` line: it is the mistake a person makes
  // when they put the machine line under the prose where it reads better.
  if (/^pxm( |$)/.test(lines[i])) {
    fail(where, i + 1, 'the `pxm` version line is the first line of the file, ahead of the header comments');
  }
  const m = /^shape ([a-zA-Z][a-zA-Z0-9_]*) (\d+) (\d+)$/.exec(lines[i]);
  if (m === null) fail(where, i + 1, `expected \`shape <name> <w> <h>\`, got ${JSON.stringify(lines[i])}`);
  const name = m[1];
  const w = Number(m[2]);
  const h = Number(m[3]);
  i++;

  const legend: [string, string][] = [];
  const pools: LegendPool[] = [];
  const seen = new Set<string>();
  // Set once the first `@pool` legend line is seen. A plain legend line after
  // that point is refused (R56) — see `LegendPool`'s docblock for why: two
  // arrays can only reproduce one file order if every document orders them
  // the same way, the same discipline `anchor`/`feel`/`node` already state.
  let sawPool = false;
  while (i < lines.length && lines[i].startsWith('legend ')) {
    const pm = /^legend (\S) @pool((?: \S+)*)$/.exec(lines[i]);
    if (pm !== null) {
      const ch = pm[1];
      if (ch === VOID_CHAR) fail(where, i + 1, `\`${VOID_CHAR}\` is reserved for empty and is never declared`);
      if (seen.has(ch)) fail(where, i + 1, `legend character \`${ch}\` declared twice`);
      seen.add(ch);
      sawPool = true;
      const entries: LegendPoolEntry[] = [];
      const clsSeen = new Set<DeferredClass>();
      for (const tok of pm[2].split(' ')) {
        if (tok === '') continue;
        const em = /^(@[a-z][a-z0-9]*):(\S+)$/.exec(tok);
        if (em === null) {
          fail(where, i + 1, `pool for \`${ch}\`: expected \`@class:weight\`, got ${JSON.stringify(tok)}`);
        }
        const cls = deferredClassOf(em[1]);
        if (cls === undefined) {
          fail(where, i + 1, `unknown class \`${em[1]}\` — the format has ${DEFERRED_CLASSES.map((c) => DEFERRED_PREFIX + c).join(', ')}`);
        }
        if (clsSeen.has(cls)) fail(where, i + 1, `pool for \`${ch}\` names \`${em[1]}\` twice`);
        clsSeen.add(cls);
        const weight = intToken(where, i + 1, 'weight', em[2]);
        if (weight < 1) fail(where, i + 1, `pool for \`${ch}\`: \`${em[1]}\` weight must be a whole number, one or more`);
        entries.push({ cls, weight });
      }
      if (entries.length < 2) {
        fail(where, i + 1, `pool for \`${ch}\`: a pool needs at least two entries — one entry has nothing to roll against`);
      }
      pools.push({ ch, entries });
      i++;
      continue;
    }
    if (sawPool) fail(where, i + 1, 'every plain `legend` line comes before every `@pool` legend line');
    const lm = /^legend (\S) (@[a-z][a-z0-9]*|[A-Z][A-Z0-9_]*)$/.exec(lines[i]);
    if (lm === null) {
      fail(where, i + 1, `expected \`legend <char> <MATERIAL|@class>\`, got ${JSON.stringify(lines[i])}`);
    }
    // A class is a word in the format, not a name from the build, so an unknown
    // one is a syntax error here rather than a resolution error three modules
    // away. Materials are deliberately *not* checked — see the header.
    if (lm[2].startsWith(DEFERRED_PREFIX) && deferredClassOf(lm[2]) === undefined) {
      fail(where, i + 1, `unknown class \`${lm[2]}\` — the format has ${DEFERRED_CLASSES.map((c) => DEFERRED_PREFIX + c).join(', ')}`);
    }
    if (lm[1] === VOID_CHAR) fail(where, i + 1, `\`${VOID_CHAR}\` is reserved for empty and is never declared`);
    if (seen.has(lm[1])) fail(where, i + 1, `legend character \`${lm[1]}\` declared twice`);
    seen.add(lm[1]);
    legend.push([lm[1], lm[2]]);
    i++;
  }
  if (legend.length === 0 && pools.length === 0) {
    fail(where, i + 1, 'no `legend` lines — a mask of nothing but void is not a shape');
  }

  // Anchors follow the legend and precede the blank line. They are declarations
  // about the drawing, so they sit with the other declarations; the grid begins
  // at the blank line and runs to the end of the file.
  const anchors: ShapeAnchor[] = [];
  const anchorLine: number[] = [];
  const named = new Set<string>();
  while (i < lines.length && lines[i].startsWith('anchor ')) {
    const am = /^anchor (\S+) (\S+) (\S+) (\S+)((?: \S+)*)$/.exec(lines[i]);
    if (am === null) {
      fail(where, i + 1, `expected \`anchor <name> <x> <y> <role> [claim...]\`, got ${JSON.stringify(lines[i])}`);
    }
    const x = intToken(where, i + 1, 'x', am[2]);
    const y = intToken(where, i + 1, 'y', am[3]);
    const claims: AnchorClaim[] = [];
    for (const tok of am[5].split(' ')) {
      if (tok === '') continue;
      const cm = /^([a-z]+)(?:=(\S+))?$/.exec(tok);
      if (cm === null) fail(where, i + 1, `expected a claim, got ${JSON.stringify(tok)}`);
      claims.push({
        name: cm[1] as AnchorClaimName,
        n: cm[2] === undefined ? null : intToken(where, i + 1, cm[1], cm[2]),
      });
    }
    if (named.has(am[1])) fail(where, i + 1, `anchor \`${am[1]}\` declared twice — a name addresses one place`);
    named.add(am[1]);
    anchors.push({ name: am[1], x, y, role: am[4] as AnchorRole, claims });
    anchorLine.push(i + 1);
    i++;
  }
  // Ordering, stated rather than left to whichever loop ran out first: a legend
  // line down here would otherwise be reported as a missing blank line, which
  // names the symptom and not the mistake.
  if (i < lines.length && lines[i].startsWith('legend ')) {
    fail(where, i + 1, 'every `legend` line comes before every `anchor` line');
  }

  // `feel` — a weapon's one authored value — and `node` — its per-combo-key
  // timing/swing overrides — follow anchors and precede the grid, version 3
  // (issue #168 phase 1). Absent for every mask and tile ever shipped.
  let feel: WeaponFeel | undefined;
  if (i < lines.length && lines[i].startsWith('feel ')) {
    const fm = /^feel weight (\S+)$/.exec(lines[i]);
    if (fm === null) fail(where, i + 1, `expected \`feel weight <n>\`, got ${JSON.stringify(lines[i])}`);
    feel = { weight: decimalToken(where, i + 1, 'weight', fm[1]) };
    i++;
  }
  if (i < lines.length && lines[i].startsWith('feel ')) {
    fail(where, i + 1, 'a weapon has one `feel` line, not two');
  }
  if (i < lines.length && (lines[i].startsWith('legend ') || lines[i].startsWith('anchor '))) {
    fail(where, i + 1, 'every `legend`/`anchor` line comes before the `feel` line');
  }

  const nodes: WeaponNodeDecl[] = [];
  const nodeNamed = new Set<string>();
  while (i < lines.length && lines[i].startsWith('node ')) {
    const parts = lines[i].split(' ');
    if (parts.length < 3) fail(where, i + 1, `expected \`node <key> <field> <value>...\`, got ${JSON.stringify(lines[i])}`);
    const key = parts[1];
    if (nodeKeyOrder(key) < 0) fail(where, i + 1, `node: ${JSON.stringify(key)} is not one of ${WEAPON_NODE_KEYS.join(', ')}`);
    if (nodeNamed.has(key)) fail(where, i + 1, `node \`${key}\` declared twice — a node is named once`);
    nodeNamed.add(key);

    const decl: { -readonly [K in keyof WeaponNodeDecl]?: WeaponNodeDecl[K] } = {};
    // `ease` sorts after every numeric field — `NODE_NUMERIC_FIELDS.length`
    // rather than a name comparison, so "ease last" is a fact about the
    // order rather than an accident of where the string `ease` sorts.
    let fieldOrder = -1;
    let p = 2;
    while (p < parts.length) {
      const field = parts[p];
      if (p + 1 >= parts.length) fail(where, i + 1, `node \`${key}\`: \`${field}\` has no value`);
      const value = parts[p + 1];
      const at = field === 'ease' ? NODE_NUMERIC_FIELDS.length : (NODE_NUMERIC_FIELDS as readonly string[]).indexOf(field);
      if (at < 0) {
        fail(where, i + 1, `node \`${key}\`: ${JSON.stringify(field)} is not a field — the format has ${NODE_NUMERIC_FIELDS.join(', ')}, ease`);
      }
      if (at === fieldOrder) fail(where, i + 1, `node \`${key}\`: \`${field}\` is set twice`);
      if (at < fieldOrder) {
        fail(where, i + 1, `node \`${key}\`: fields are written in the order ${NODE_NUMERIC_FIELDS.join(' ')}, ease last`);
      }
      fieldOrder = at;
      if (field === 'ease') {
        if (!(WEAPON_EASE_KINDS as readonly string[]).includes(value)) {
          fail(where, i + 1, `node \`${key}\`: ease ${JSON.stringify(value)} is not one of ${WEAPON_EASE_KINDS.join(', ')}`);
        }
        decl.ease = value as WeaponEaseKind;
      } else {
        decl[field as NodeNumericField] = decimalToken(where, i + 1, field, value);
      }
      p += 2;
    }
    if (Object.keys(decl).length === 0) fail(where, i + 1, `node \`${key}\`: a node line sets at least one field`);
    nodes.push({ key: key as WeaponNodeKey, ...decl });
    i++;
  }
  if (i < lines.length && (lines[i].startsWith('legend ') || lines[i].startsWith('anchor ') || lines[i].startsWith('feel '))) {
    fail(where, i + 1, 'every `feel` line comes before every `node` line');
  }

  if (i >= lines.length || lines[i] !== '') {
    fail(where, i + 1, 'expected exactly one blank line between the declarations and the grid');
  }
  i++;

  const rows = lines.slice(i);
  if (rows.length !== h) fail(where, i + 1, `${rows.length} grid rows for a height of ${h}`);
  for (let r = 0; r < rows.length; r++) {
    if (rows[r].length !== w) {
      fail(where, i + r + 1, `row ${r} is ${rows[r].length} characters wide, expected ${w}`);
    }
    for (const ch of rows[r]) {
      if (ch !== VOID_CHAR && !seen.has(ch)) fail(where, i + r + 1, `character \`${ch}\` is not in the legend`);
    }
  }

  // An unused legend entry is not harmless: it is the residue of an edit, and
  // it silently breaks canonical form, so `serialize(parse(t)) === t` would
  // fail on it later and further from the cause.
  const used = new Set<string>();
  for (const row of rows) for (const ch of row) used.add(ch);
  for (const [ch] of legend) {
    if (!used.has(ch)) fail(where, i, `legend character \`${ch}\` appears nowhere in the grid`);
  }
  for (const p of pools) {
    if (!used.has(p.ch)) fail(where, i, `legend character \`${p.ch}\` appears nowhere in the grid`);
  }

  const doc: ShapeMaskDoc = { header, name, w, h, legend, pools, anchors, feel, nodes, rows };

  // The claims, now that there is a grid to check them against. Refused rather
  // than reported: a `.pxm` that carries a claim cannot be wrong about it, and
  // that is the whole difference between an anchor and a comment.
  for (let a = 0; a < anchors.length; a++) {
    const p = anchorProblem(w, h, rows, anchors[a]);
    if (p !== null) fail(where, anchorLine[a], p);
  }

  // **The declared version is the one the content needs — not more, not less.**
  // Both directions are errors and for the same reason: the version is derived
  // (see `pxmVersion`), so a document has exactly one legal spelling of its own
  // version line, and accepting either mismatch would give it two.
  const need = pxmVersion(doc);
  if (declaredVersion > need) {
    fail(
      where, 1,
      `this file declares .pxm version ${declaredVersion} and uses nothing newer than version ${need} — ` +
        'the version line states the lowest version that can read the file',
    );
  }
  if (declaredVersion < need) {
    fail(where, 1, `this document uses .pxm version ${need} grammar, so it must open with \`pxm ${need}\``);
  }
  return doc;
}

/** Write a document back out. The inverse of `parseShapeMask`, exactly. */
/** Write one `node` line back out, fields in the one fixed order. */
function nodeText(n: WeaponNodeDecl): string {
  const parts = [`node`, n.key];
  for (const f of NODE_NUMERIC_FIELDS) {
    const v = n[f];
    if (v !== undefined) parts.push(f, String(v));
  }
  if (n.ease !== undefined) parts.push('ease', n.ease);
  return parts.join(' ');
}

/** Write one legend pool back out, entries in file order — never weight or alphabetical order. */
function poolText(p: LegendPool): string {
  const entries = p.entries.map((e) => `${DEFERRED_PREFIX}${e.cls}:${String(e.weight)}`);
  return ['legend', p.ch, '@pool', ...entries].join(' ');
}

export function serializeShapeMask(doc: ShapeMaskDoc): string {
  const out: string[] = [];
  const version = pxmVersion(doc);
  if (version > 1) out.push(`pxm ${String(version)}`);
  for (const line of doc.header) out.push(line);
  out.push(`shape ${doc.name} ${doc.w} ${doc.h}`);
  for (const [ch, mat] of doc.legend) out.push(`legend ${ch} ${mat}`);
  for (const p of doc.pools) out.push(poolText(p));
  for (const a of doc.anchors) out.push(anchorText(a));
  if (doc.feel !== undefined) out.push(`feel weight ${String(doc.feel.weight)}`);
  for (const n of doc.nodes) out.push(nodeText(n));
  out.push('');
  for (const row of doc.rows) out.push(row);
  return out.join('\n') + '\n';
}

/**
 * Resolve a document into the grid of material ids `bakeShape` takes.
 *
 * `resolve` is passed in rather than imported so this stays callable before
 * `registerMaterials()` in a tool that only wants the geometry, and so a
 * document naming a material the build does not have fails here — with the
 * shape's name attached — instead of baking a hole.
 *
 * **A deferred class is refused here**, and the refusal is the point rather than
 * an omission: a rigid body is carried around the world by the physics, so there
 * is no "host biome" for it to be made of, and a crate whose planks were
 * whatever rock it was last resting on is not a thing anyone wants. The tile
 * side takes the other half of the vocabulary and refuses materials, so between
 * them every legend value has exactly one consumer that accepts it.
 */
export function specFor(doc: ShapeMaskDoc, resolve: (name: string) => MatId | undefined): ShapeSpec {
  if (doc.pools.length > 0) {
    // Same reasoning as the deferred-class refusal just below: a pool is
    // resolved per placement, against a host, and a rigid body carried
    // around the world by the physics has no host to roll it against.
    throw new MaskError(`shape ${doc.name}: this document has a legend pool, and a rigid shape has no host to roll it against`);
  }
  const byChar = new Map<string, MatId>();
  for (const [ch, matName] of doc.legend) {
    if (deferredClassOf(matName) !== undefined) {
      throw new MaskError(
        `shape ${doc.name}: \`${matName}\` is a deferred class, and a rigid shape has no host to defer to`,
      );
    }
    const id = resolve(matName);
    if (id === undefined) throw new MaskError(`shape ${doc.name}: unknown material ${matName}`);
    byChar.set(ch, id);
  }
  const cells: MatId[] = new Array(doc.w * doc.h);
  for (let y = 0; y < doc.h; y++) {
    for (let x = 0; x < doc.w; x++) {
      const ch = doc.rows[y][x];
      cells[y * doc.w + x] = ch === VOID_CHAR ? VOID : (byChar.get(ch) as MatId);
    }
  }
  return { name: doc.name, w: doc.w, h: doc.h, cells };
}

/**
 * Build a canonical document from a grid of material names, `null` for empty.
 *
 * This is the *editor's* side of the format: a tool that has been drawing into
 * a grid calls this and then `serializeShapeMask`, and the file it writes is
 * one the parser accepts unchanged. Legend characters are assigned
 * deterministically — first letter of the material name, then the next unused
 * letter of it, then a digit — so that two runs of the same edit produce the
 * same bytes and a diff shows only what was drawn.
 *
 * A deferred class is spelled `@host` and its letters are drawn from `HOST`:
 * the sigil is stripped and the rest upper-cased before the letters are walked,
 * so a tile's grid reads `H`, `S`, `P`, `G` rather than `@` and `h`. That is a
 * no-op for every material — the names are already upper-case and contain no
 * sigil — so no shipped `.pxm` moves a byte, which `tests/shapeMask.test.ts`
 * §1 holds on the files themselves.
 */
export function docFromGrid(
  name: string,
  w: number,
  h: number,
  grid: readonly (string | null)[],
  header: readonly string[] = [],
  anchors: readonly ShapeAnchor[] = [],
  feel?: WeaponFeel,
  nodes: readonly WeaponNodeDecl[] = [],
): ShapeMaskDoc {
  if (grid.length !== w * h) throw new MaskError(`docFromGrid: ${grid.length} cells for ${w}x${h}`);
  const charFor = new Map<string, string>();
  const taken = new Set<string>([VOID_CHAR]);
  const legend: [string, string][] = [];
  const assign = (matName: string): string => {
    const have = charFor.get(matName);
    if (have !== undefined) return have;
    let ch: string | undefined;
    const letters = matName.startsWith(DEFERRED_PREFIX)
      ? matName.slice(DEFERRED_PREFIX.length).toUpperCase()
      : matName;
    for (const c of letters) {
      if (!taken.has(c)) {
        ch = c;
        break;
      }
    }
    if (ch === undefined) {
      for (const c of 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789') {
        if (!taken.has(c)) {
          ch = c;
          break;
        }
      }
    }
    if (ch === undefined) throw new MaskError(`docFromGrid: out of legend characters at ${matName}`);
    taken.add(ch);
    charFor.set(matName, ch);
    legend.push([ch, matName]);
    return ch;
  };

  const rows: string[] = [];
  for (let y = 0; y < h; y++) {
    let row = '';
    for (let x = 0; x < w; x++) {
      const mat = grid[y * w + x];
      row += mat === null ? VOID_CHAR : assign(mat);
    }
    rows.push(row);
  }
  if (legend.length === 0) throw new MaskError(`docFromGrid: ${name} is entirely empty`);
  const doc: ShapeMaskDoc = {
    // The editor does not draw pools — `pools` is always empty out of here.
    header: [...header], name, w, h, legend, pools: [], anchors: [...anchors], feel, nodes: [...nodes], rows,
  };
  // **The anchors are re-checked against the grid that was just drawn, and this
  // is where the editor finds out.** Every drawing verb rebuilds the document
  // through here, so digging out the cell under a `floor` anchor is refused at
  // the moment of the stroke rather than at the next load — which is the same
  // rule the parser enforces, spelled once and called from both sides.
  const problems = anchorProblems(doc);
  if (problems.length > 0) throw new MaskError(`docFromGrid: ${name}: ${problems[0]}`);
  return doc;
}
